import { TWhereProps, firebaseService } from 'services';
import type { ICourseData, ICourseDataDB } from 'services/course.service';
import type { IUserDataDB } from 'services/user.service';

import { courseConverter } from './courseConverter';

import { ECommonErrorTypes } from 'types';
import userCourseProgress from '../UserCourseProgress';

class Course {
  public async get(id: string): Promise<ICourseData> {
    const courseDataDB = await firebaseService.getDoc<ICourseDataDB>(firebaseService.Collections.Course, id);

    if (!courseDataDB) {
      throw new Error(ECommonErrorTypes.FailedToFindData);
    }

    return await courseConverter.fromFirestore(courseDataDB);
  }

  public async getAll(filter: { ids?: string[], userId?: string }): Promise<ICourseData[]> {
    let usersCoursesIds: string[] | undefined;
    if (filter.userId) {
      const user = await firebaseService.getDoc<IUserDataDB>(firebaseService.Collections.User, filter.userId);
      if (!user) {
        throw new Error(ECommonErrorTypes.FailedToFindData);
      }
      const progresses = await userCourseProgress.getAll(user.email);
      usersCoursesIds = progresses
        .filter(p => p.progress.course.lastVisitedAt !== null)
        .map(p => p.courseId);
      if (usersCoursesIds.length === 0) {
        return [];
      }
    }
    const queryConstraints: TWhereProps = [
      (filter.ids || usersCoursesIds) && { param: 'id' as const, value: [...filter.ids ?? [], ...usersCoursesIds ?? []], operator: 'in' as const },
    ].filter(c => c !== undefined);

    const coursesDataDB = (await firebaseService.getDocs<ICourseDataDB>(firebaseService.Collections.Course, queryConstraints)).map(d => d.data);
    return await Promise.all(coursesDataDB.map(courseDataDB => courseConverter.fromFirestore(courseDataDB)));
  }

  public async set(id: string, courseData: ICourseData): Promise<ICourseData> {
    const courseDataDB = courseConverter.toFirestore(courseData);
    const newCourseDataDB = await firebaseService.setDoc<ICourseDataDB>(firebaseService.Collections.Course, id, courseDataDB);

    if (!newCourseDataDB) {
      throw new Error('Failed to update course data');
    }

    return await courseConverter.fromFirestore(newCourseDataDB);
  }
};

export default new Course();
